import '@/shared/App.css'
import '@/shared/styles/permission-error.css'
import { Toaster } from "@/shared/components/ui/toaster"
import { Toaster as Sonner } from "sonner"
import PermissionErrorHandler, { showPermissionError, isPermissionError } from "@/shared/components/PermissionErrorHandler"
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useEffect, Component } from 'react'
import { initErrorSuppressor } from '@/shared/utils/errorSuppressor'
import { LanguageProvider } from '@/shared/components/shared/LanguageContext'

// ErrorBoundary to catch and surface render crashes visually
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary]', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, fontFamily: 'monospace', background: '#fff0f0', minHeight: '100vh' }}>
          <h1 style={{ color: 'red' }}>Something went wrong</h1>
          <pre style={{ whiteSpace: 'pre-wrap', color: '#333' }}>
            {this.state.error?.toString()}
          </pre>
          <details style={{ marginTop: 16 }}>
            <summary>Component Stack</summary>
            <pre style={{ whiteSpace: 'pre-wrap', color: '#666' }}>
              {this.state.errorInfo?.componentStack}
            </pre>
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}

// Create a client with global error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry on permission errors
        if (isPermissionError(error)) {
          return false;
        }
        return failureCount < 1;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      onError: (error) => {
        // Handle permission errors globally for queries
        if (isPermissionError(error)) {
          showPermissionError(error);
        }
      },
    },
    mutations: {
      onError: (error) => {
        // Handle permission errors globally for mutations
        if (isPermissionError(error)) {
          showPermissionError(error);
        } else if (error.message?.includes('Session expired')) {
          toast.error('Session Expired', {
            description: 'Please login again to continue.',
            duration: 4000,
          });
        } else if (error.message?.includes('Foreign key constraint') || 
                   error.message?.includes('constraint violated')) {
          toast.error('Update Failed', {
            description: 'Unable to complete this action. Please try again or contact support.',
            duration: 5000,
          });
        }
      },
    },
  },
})

export default function AppShell({ children }) {
  // Initialize error suppressor to prevent browser alerts
  useEffect(() => {
    initErrorSuppressor();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <ErrorBoundary>
          <PermissionErrorHandler />
          {children}
          <Toaster />
          <Sonner 
            position="top-center"
            expand={true}
            richColors
            closeButton
            theme="light"
            toastOptions={{
              duration: 5000,
              className: 'custom-toast',
            }}
          />
        </ErrorBoundary>
      </LanguageProvider>
    </QueryClientProvider>
  )
}

